/**
 * Ô nhập ngày sinh / ngày dịp "Ngày · Tháng · Năm (không bắt buộc)" (QĐ 0480,
 * CEO chốt 5a 28/10/2026). Thay lịch chọn ngày: lùi hàng chục năm trên lịch để
 * tìm năm sinh là lý do CSKH bỏ trống ô này.
 *
 * Bàn phím số trên điện thoại; gõ đủ 2 số ngày tự nhảy sang ô tháng. Giá trị
 * ra là YYYY-MM-DD (năm trống ⇒ 1904 = không rõ năm), "" khi bỏ trống cả ba ô
 * hoặc đang gõ dở. Nơi dùng PHẢI giữ đúng `iso` ô phát ra (kể cả "") và chặn
 * lưu theo `error`: giữ lại ngày cũ là ô tưởng giá trị đổi từ ngoài, nạp đè
 * chữ đang gõ.
 */
import { useId, useRef, useState } from "react";
import { Input } from "@/ui/input";
import { evaluateDayMonth, parseBirthday, type DayMonthValue } from "@/lib/birthday";
import { cn } from "@/lib/utils";

export type { DayMonthValue };

function partsOf(iso: string): { d: string; m: string; y: string } {
  const p = parseBirthday(iso);
  if (!p) return { d: "", m: "", y: "" };
  return { d: String(p.day), m: String(p.month), y: p.year == null ? "" : String(p.year) };
}

const digits = (s: string, max: number) => s.replace(/\D/g, "").slice(0, max);

export function DayMonthInput({
  value,
  onChange,
  label,
  disabled,
  showYear = true,
  className,
}: {
  /** YYYY-MM-DD (năm 1904 = không rõ năm) hoặc "". */
  value: string;
  onChange: (next: DayMonthValue) => void;
  /** Tên ô cho trình đọc màn hình ("Sinh nhật"…). */
  label: string;
  disabled?: boolean | undefined;
  /** Ẩn ô năm cho dịp không cần năm. */
  showYear?: boolean;
  className?: string | undefined;
}) {
  const id = useId();
  const [parts, setParts] = useState(() => partsOf(value));
  // Giá trị gần nhất ô này phát ra (hoặc đã nạp). Giá trị đổi từ ngoài (mở hộp
  // cho đơn khác, nút "Lưu sinh nhật này") ⇒ nạp lại ba ô ngay lúc vẽ.
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setParts(partsOf(value));
  }
  const monthRef = useRef<HTMLInputElement>(null);

  const update = (next: { d: string; m: string; y: string }) => {
    setParts(next);
    const out = evaluateDayMonth(next.d, next.m, next.y);
    setSynced(out.iso);
    onChange(out);
  };
  const shown = evaluateDayMonth(parts.d, parts.m, parts.y);

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center gap-1.5" role="group" aria-label={label}>
        <Input
          id={`${id}-d`}
          aria-label={`${label} — ngày`}
          inputMode="numeric"
          placeholder="Ngày"
          className="w-16 text-center"
          value={parts.d}
          disabled={disabled}
          onChange={(e) => {
            const d = digits(e.target.value, 2);
            update({ ...parts, d });
            if (d.length === 2) monthRef.current?.focus();
          }}
        />
        <span className="text-muted-foreground" aria-hidden>
          /
        </span>
        <Input
          ref={monthRef}
          id={`${id}-m`}
          aria-label={`${label} — tháng`}
          inputMode="numeric"
          placeholder="Tháng"
          className="w-16 text-center"
          value={parts.m}
          disabled={disabled}
          onChange={(e) => update({ ...parts, m: digits(e.target.value, 2) })}
        />
        {showYear ? (
          <>
            <span className="text-muted-foreground" aria-hidden>
              /
            </span>
            <Input
              id={`${id}-y`}
              aria-label={`${label} — năm (không bắt buộc)`}
              inputMode="numeric"
              placeholder="Năm (nếu biết)"
              className="w-32 min-w-0"
              value={parts.y}
              disabled={disabled}
              onChange={(e) => update({ ...parts, y: digits(e.target.value, 4) })}
            />
          </>
        ) : null}
      </div>
      {shown.error ? (
        <p role="alert" className="text-xs text-[var(--color-danger-text)]">
          {shown.error}
        </p>
      ) : null}
    </div>
  );
}
